import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import { DataGridPro } from "@mui/x-data-grid-pro";
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import { copyDemandTableToClipboard } from '../Utilities/tableUtils';
import { formatWeekHeader } from '../Utilities/weekNumberUtils';
import { useColumnWidths } from '../Utilities/columnWidthUtils';
import './SkuBaseForecastTable.css';

interface UnitsByWeekEntry {
    totalUnits: number;
    weekMonth: number;
}

interface UnitsByWeekMap {
    [week: string]: UnitsByWeekEntry;
}

interface PeriodicDemandRow {
    skuNumber: string;
    skuDescription: string;
    units: UnitsByWeekMap;
}

interface SummaryData {
    periodicDemands: PeriodicDemandRow[];
    weekToMonthMap: { [week: string]: number };
}

interface OutputsDemandTableProps {
    summaryData: SummaryData;
}

const OutputsDemandTable = ({ summaryData }: OutputsDemandTableProps) => {
    const defaultColumnWidths = { skuNumber: 100, skuDescription: 200 };
    const { columnWidths, handleColumnResize } = useColumnWidths('outputsDemand', defaultColumnWidths);

    const copyToClipboard = async () => {
        await copyDemandTableToClipboard(summaryData, 'weekly');
    };

    // UseMemo to create columns, column grouping, and rows
    const { columns, columnGroupingModel, rows } = useMemo(() => {
        if (!summaryData || !summaryData.periodicDemands || summaryData.periodicDemands.length === 0) {
            return {
                columns: [] as any[],
                columnGroupingModel: [] as any[],
                rows: [] as any[],
            };
        }

        const weeks = Object.keys(summaryData.weekToMonthMap);

        // Group weeks by `WeekMonth`
        const monthMap = weeks.reduce((acc: Record<number, string[]>, week) => {
            const month = summaryData.weekToMonthMap[week];
            if (!acc[month]) acc[month] = [];
            acc[month].push(week);
            return acc;
        }, {} as Record<number, string[]>);

        // Generate columns for weeks
        const weekColumns = weeks.map((week, index) => ({
            field: week,
            headerName: formatWeekHeader(week, index),
            type: "number",
            width: 100,
            valueGetter: (value: unknown, row: any) =>
                row && row.units && row.units[week]?.totalUnits || 0,
        }));

        // Grouping model for column headers
        const columnGroupingModel = [
            {
                groupId: 'skuGroup',
                headerName: '',
                children: [{ field: 'skuNumber' }]
            },
            {
                groupId: 'descGroup',
                headerName: '',
                children: [{ field: 'skuDescription' }]
            },
            ...Object.entries(monthMap).map(([month, monthWeeks]) => ({
                groupId: month,
                headerName: new Date(2000, Number(month) - 1, 1).toLocaleString("default", { month: "short" }).toUpperCase(),
                children: monthWeeks.map((w) => ({ field: w })),
            })),
            {
                groupId: 'fyGroup',
                headerName: '',
                children: [{ field: 'fyTotal' }]
            }
        ];

        // Add a full-year (FY) total column
        const fyColumn = {
            field: "fyTotal",
            headerName: "FY Total",
            type: "number",
            width: 120,
            valueGetter: (value: unknown, row: any) =>
                row && row.units
                    ? Object.values(row.units).reduce((sum: number, entry: any) => sum + entry.totalUnits, 0)
                    : 0,
        };

        // Base columns (SKU Description and Week Columns)
        const baseColumns = [
            { field: "skuNumber", headerName: "SKU", width: columnWidths.skuNumber },
            { field: "skuDescription", headerName: "Description", width: columnWidths.skuDescription },
            ...weekColumns,
            fyColumn,
        ];

        // Rows for the DataGrid
        const rows = summaryData.periodicDemands.map((demand, index) => ({
            id: index, // DataGrid requires a unique ID
            skuNumber: demand.skuNumber || "Unknown SKU",
            skuDescription: demand.skuDescription || "Unknown SKU",
            units: demand.units || {},
        }));

        return {
            columns: baseColumns,
            columnGroupingModel,
            rows,
        };
    }, [summaryData, columnWidths]);

    // Render fallback message for no data
    if (rows.length === 0) {
        return <p>No data available</p>;
    }

    return (
        <Box sx={{ height: "100%", width: "100%", position: 'relative' }}>
            {/* Copy button positioned absolutely in the top-left corner */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    zIndex: 1000,
                    border: 'none',
                    borderRadius: '4px',
                    padding: '2px'
                }}
            >
                <Tooltip title="Copy table to clipboard">
                    <IconButton
                        onClick={copyToClipboard}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <DataGridPro
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                onColumnResize={handleColumnResize}
                initialState={{
                    pinnedColumns: { left: ['skuNumber', 'skuDescription'] },
                    pagination: { paginationModel: { pageSize: 25, page: 0 } }
                }}
            />
        </Box>
    );
};

export default OutputsDemandTable;


